'use client'

import { useTranslations } from 'next-intl'
import Link from 'next/link'

import { Skeleton } from '@/components/ui/skeleton'
import { isFreePlanLabel } from '@/lib/billing/capabilities'
import { BILLING_ROUTE } from '@/lib/billing/routes'
import { cn } from '@/lib/utils'
import { formatFirstLetterToUpperCase } from '@/lib/utils/format-util'
import { useSubscription } from '@/service/billing.hook'

/**
 * Top-right plan status badge. Renders the server-provided `plan_label` verbatim
 * ("ESSENTIAL" / "PRO" / "CUSTOM") and links to the billing page. Accent for any
 * paid tier (PRO or an operator-comped CUSTOM), grey for the free tier. Renders a same-sized
 * skeleton until the subscription has loaded — never a wrong tier, but also never
 * an empty slot: a `null` render here would let a `flex-1` sibling (e.g. the
 * account button in the sidebar footer) expand into this spot, so a click meant
 * for the badge would land on the sibling instead until the data arrives.
 */
export function PlanBadge() {
  const t = useTranslations('billing')
  const { data } = useSubscription()
  const label = data?.planLabel
  if (!label) return <Skeleton className="h-6 w-12 shrink-0" aria-hidden="true" />

  const accent = !isFreePlanLabel(label)

  return (
    <Link
      href={BILLING_ROUTE}
      aria-label={`${t('menuItem')}: ${label}`}
      className={cn(
        'inline-flex h-6 shrink-0 items-center border px-2 text-sm font-medium tracking-tight transition-colors',
        accent
          ? 'border-foreground/25 text-ox-cyan hover:border-ox-cyan'
          : 'text-foreground/60 border-white/20 hover:border-white/40'
      )}
    >
      {formatFirstLetterToUpperCase(label)}
    </Link>
  )
}
